import { useState } from "react";
import { formatPrice, getCategoryAvatar } from "./helpers";
import { Avatar, Box, Button, Chip, Divider, IconButton, Rating, Tooltip, Typography } from "@mui/material";
import TemplatePreview from "./TemplatePreview";
import { ACCENT, BADGE_STYLES, GRADIENT, TEMPLATE_AVATAR_GRAD } from "./constants";
import GridViewIcon from "@mui/icons-material/GridView";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
function TemplateCard({ item, compact = false, onOpen, bundle = [], purchased = [], toggleBundle = () => {}, handleGet = () => {} }) {
  const [hovered, setHovered] = useState(false);
  const { icon: CatIcon } = getCategoryAvatar(item.category);
  const inBundle = bundle.includes(item.id);
  const owned  = purchased.includes(item.id);

  if (compact) {
    return (
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => onOpen(item)}
        sx={{ width: 300, height: 200, flexShrink: 0, perspective: "1200px", cursor: "pointer" }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            transformStyle: "preserve-3d",
            transition: "transform 0.65s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: hovered ? "rotateY(180deg)" : "rotateY(0deg)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              borderRadius: 2,
              overflow: "hidden",
              backgroundColor: "#f0f0f0",
              boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
            }}
          >
            <TemplatePreview item={item} />
          </Box>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
              borderRadius: 2,
              backgroundColor: "#fff",
              boxShadow: "0 8px 28px rgba(0,0,0,0.16)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 0.8,
              px: 2.5,
              py: 2,
            }}
          >
            <Typography fontWeight={800} fontSize="0.97rem" textAlign="center" sx={{ color: "#1a1a1a", lineHeight: 1.3 }}>
              {item.name}
            </Typography>
            {item.category && (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.2 }}>
                {item.category}
              </Typography>
            )}
            <Rating value={item.rating} precision={0.5} readOnly size="small" />
            <Typography fontWeight={800} fontSize="1rem" sx={{ color: item.price === 0 ? "#2e7d32" : "#1a1a1a" }}>
              {formatPrice(item.price)}
            </Typography>
            {item.badge && BADGE_STYLES[item.badge] && (
              <Chip
                label={item.badge}
                size="small"
                sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700, backgroundColor: BADGE_STYLES[item.badge].bg, color: BADGE_STYLES[item.badge].color }}
              />
            )}
            <Typography variant="caption" sx={{ mt: 0.5, color: "#aaa", fontSize: "0.68rem" }}>
              Click to view details
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }
  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onOpen(item)}
      sx={{ height: 230, perspective: "1200px", cursor: "pointer" }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          transformStyle: "preserve-3d",
          transition: "transform 0.65s cubic-bezier(0.4, 0, 0.2, 1)",
          transform: hovered ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            borderRadius: 2,
            overflow: "hidden",
            backgroundColor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ position: "absolute", top: 0, right: 0, zIndex: 1, width: 22, height: 22,  backgroundColor: ACCENT, display: "flex", alignItems: "center", justifyContent: "center", borderBottomLeftRadius: "50%", padding: 1 }}>
            <GridViewIcon sx={{ fontSize: 13, color: "#fff" }} />
          </Box>
          <Box sx={{ flex: 1, px: 2, pt: 2, pb: 1, overflow: "hidden" }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
              <Avatar
                variant="rounded"
                sx={{ width: 44, height: 44, borderRadius: 1.5, flexShrink: 0, background: TEMPLATE_AVATAR_GRAD }}
              >
                <CatIcon sx={{ fontSize: 22, color: "#fff" }} />
              </Avatar>
              <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.4, width: "100%", alignItems: "flex-start" }}>
                <Box sx={{ display: "flex", width: "100%", alignItems: "flex-start" }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3, color: "#1a1a1a" }}>
                    {item.name}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  {item.category}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.8 }}>
              <Rating value={item.rating} precision={0.5} readOnly size="small" />
              <Typography variant="caption" color="text.secondary">({item.reviews})</Typography>
            </Box>
            <Tooltip title={item.description} placement="top" arrow>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.5, mb: 1, cursor: "default", textAlign: "left" }}
              >
                {item.description}
              </Typography>
            </Tooltip>
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography fontWeight={700} fontSize="0.9rem" sx={{ color: item.price === 0 ? "#2e7d32" : "#1a1a1a" }}>
              {formatPrice(item.price)}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
              {owned ? (
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: "0.95rem !important", color: "#2e7d32 !important" }} />}
                  label="Installed"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ height: 24, fontSize: "0.7rem" }}
                />
              ) : (
                <>
                  <Tooltip title={inBundle ? "Remove from Cart" : "Add to Cart"} arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); toggleBundle(item.id); }}
                      sx={{ border: "1px solid", borderColor: inBundle ? ACCENT : "#ccc", color: inBundle ? ACCENT : "#aaa", borderRadius: 1, p: 0.5 }}
                    >
                      {inBundle ? <RemoveShoppingCartIcon sx={{ fontSize: 15 }} /> : <AddShoppingCartIcon sx={{ fontSize: 15 }} />}
                    </IconButton>
                  </Tooltip>
                  <Button
                    size="small"
                    disableElevation
                    onClick={(e) => { e.stopPropagation(); handleGet(item); }}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.78rem",
                      background: item.price === 0 ? "linear-gradient(90deg, #388e3c, #2e7d32)" : GRADIENT,
                      color: "#fff",
                      boxShadow: "none",
                      "&:hover": { opacity: 0.92, boxShadow: "none" },
                    }}
                  >
                    {item.price === 0 ? "Get Free" : "Buy"}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            borderRadius: 2,
            overflow: "hidden",
            backgroundColor: "#f0f0f0",
            boxShadow: "0 8px 28px rgba(0,0,0,0.16)",
          }}
        >
          <TemplatePreview item={item} />
          <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, py: 0.8, px: 1.5, background: "linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%)" }}>
            <Typography variant="caption" sx={{ color: "#fff", fontSize: "0.65rem" }}>Click to open</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}


export default TemplateCard;